import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Markdown from '@/components/Markdown';

// Markdown with a TL;DR fold: agent output follows the convention
// "short summary, then a `---` divider, then details" — we render the part
// before the first standalone `---` and tuck the rest behind "Show details".
// Content that ignores the convention gets a length-based fallback: fold at
// the last paragraph boundary before `collapseAt` characters.

function splitTldr(text: string, collapseAt: number): [string, string | null] {
  // A standalone --- line (markdown hr). Table separator rows (|---|) and
  // heading underlines don't match because we require the line to be exactly
  // dashes.
  const divider = text.match(/\n[ \t]*-{3,}[ \t]*\n/);
  if (divider && divider.index !== undefined && divider.index > 0) {
    return [
      text.slice(0, divider.index),
      text.slice(divider.index + divider[0].length).trim() || null,
    ];
  }
  if (text.length <= collapseAt) return [text, null];
  // Fallback: fold at a paragraph boundary near the limit.
  const cut = text.lastIndexOf('\n\n', collapseAt);
  if (cut < collapseAt * 0.3) {
    // No usable boundary — fold at the limit mid-flow rather than not at all.
    return [`${text.slice(0, collapseAt)}…`, text];
  }
  return [text.slice(0, cut), text.slice(cut).trim() || null];
}

export default function CollapsibleMarkdown({
  children,
  className = '',
  collapseAt = 420,
}: {
  children: string;
  className?: string;
  collapseAt?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const [tldr, rest] = useMemo(() => splitTldr(children, collapseAt), [children, collapseAt]);

  if (!rest) return <Markdown className={className}>{children}</Markdown>;

  return (
    <div className={className}>
      <Markdown>{expanded ? children : tldr}</Markdown>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium text-primary-500 hover:text-primary-600"
      >
        {expanded ? (
          <>
            <ChevronUp className="w-3 h-3" /> Hide details
          </>
        ) : (
          <>
            <ChevronDown className="w-3 h-3" /> Show details
          </>
        )}
      </button>
    </div>
  );
}
